import React from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { applyVendorRentalOperation, createVendorEquipmentRental, VendorRentalInventoryError, vendorRentalInventoryKey,
  type VendorEquipmentCreation, type VendorEquipmentCreationResult, type VendorRentalOperation, type VendorRentalOperationResult } from '../data/vendorRentalInventory';
import useSessionStore from '../state/session';
import { invalidateAvailabilityQueries } from './mutations/useInventoryMutations';

export type VendorRentalWrite = { kind: 'create'; request: VendorEquipmentCreation } | { kind: 'operation'; request: VendorRentalOperation };
export type VendorRentalWriteResult = { kind: 'create'; value: VendorEquipmentCreationResult } | { kind: 'operation'; value: VendorRentalOperationResult };
const changedEvent = 'venue-vendor-rental-pending';
const scopeFor = (write: VendorRentalWrite) => write.kind === 'create' ? 'create' : write.request.subrentalId;
export default function useVendorRentalWrite(scope: string, onChanged?: (result: VendorRentalWriteResult) => void) {
  const actor = useSessionStore(state => state.profileId);
  const queryClient = useQueryClient();
  const storageKey = actor ? `vendor-rental:1:${actor}:${scope}` : null;
  const [readyKey, setReadyKey] = React.useState<string | null>(null);
  const [pending, setPending] = React.useState<VendorRentalWrite | null>(null);
  const [busy, setBusy] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const [message, setMessage] = React.useState<string | null>(null);
  const working = React.useRef(false);
  const pendingRef = React.useRef<VendorRentalWrite | null>(null);
  React.useEffect(() => {
    setReadyKey(null); setPending(null); pendingRef.current = null; setError(null); setMessage(null);
    if (!storageKey) return;
    const read = () => {
      try {
        const raw = window.sessionStorage.getItem(storageKey);
        const value = raw ? JSON.parse(raw) as VendorRentalWrite : null;
        if (value && (!['create', 'operation'].includes(value.kind) || !value.request || scopeFor(value) !== scope
          || !/^[0-9a-f-]{36}$/i.test(value.request.requestKey))) {
          throw new Error('The saved vendor operation cannot be read. Keep this session open while its receipt is reviewed.');
        }
        setPending(value); pendingRef.current = value; setReadyKey(storageKey);
      } catch (caught) { setReadyKey(null); setError(caught instanceof Error ? caught.message : 'Enable session storage to preserve vendor operation retries.'); }
    };
    read();
    const changed = (event: Event) => { if ((event as CustomEvent<string>).detail === storageKey && !working.current) read(); };
    window.addEventListener(changedEvent, changed);
    return () => window.removeEventListener(changedEvent, changed);
  }, [storageKey, scope]);
  const execute = async (write: VendorRentalWrite, retry = false): Promise<VendorRentalWriteResult | null> => {
    if (working.current || !storageKey || readyKey !== storageKey || scopeFor(write) !== scope || (!retry && pendingRef.current)) return null;
    working.current = true; setBusy(true); setError(null); setMessage(null);
    let dispatched = false;
    const announce = () => window.dispatchEvent(new CustomEvent(changedEvent, { detail: storageKey }));
    try {
      if (!retry && window.sessionStorage.getItem(storageKey)) throw new Error('Verify the earlier vendor operation before starting another.');
      window.sessionStorage.setItem(storageKey, JSON.stringify(write)); pendingRef.current = write; setPending(write); announce();
      dispatched = true;
      const result: VendorRentalWriteResult = write.kind === 'create'
        ? { kind: 'create', value: await createVendorEquipmentRental(write.request) }
        : { kind: 'operation', value: await applyVendorRentalOperation(write.request) };
      window.sessionStorage.removeItem(storageKey); pendingRef.current = null; setPending(null); announce();
      setMessage(result.kind === 'create' ? `Created Vendor Rental PO ${result.value.documentNumber}.` : 'Vendor inventory updated.');
      try {
        const ids = result.kind === 'create' ? result.value.rentals.map(row => row.subrentalId) : [result.value.subrentalId];
        invalidateAvailabilityQueries(queryClient);
        await Promise.all([queryClient.invalidateQueries({ queryKey: ['admin', 'xrentals'] }),
          ...ids.map(id => queryClient.invalidateQueries({ queryKey: vendorRentalInventoryKey(id) })),
          queryClient.invalidateQueries({ queryKey: ['admin', 'vendor-rental-units'] }),
          queryClient.invalidateQueries({ queryKey: ['admin', 'vendor-rental-roi'] })]);
        onChanged?.(result);
      } catch { setMessage('Vendor operation completed. Refresh inventory status before starting new work.'); }
      return result;
    } catch (caught) {
      if (caught instanceof VendorRentalInventoryError && caught.outcome === 'rejected') {
        try { window.sessionStorage.removeItem(storageKey); pendingRef.current = null; setPending(null); announce(); } catch { /* Preserve the exact request if storage is unavailable. */ }
      }
      setError(!dispatched ? caught instanceof Error ? caught.message : 'The browser could not save the retry record. No vendor operation was sent.'
        : caught instanceof Error ? caught.message : 'Verify this same vendor operation before starting another.');
      return null;
    } finally { working.current = false; setBusy(false); }
  };
  return { pending, busy, error, message, ready: Boolean(storageKey && readyKey === storageKey),
    run: (write: VendorRentalWrite) => execute(write), retry: () => pendingRef.current ? execute(pendingRef.current, true) : Promise.resolve(null) };
}
